import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CreditCard } from "lucide-react";

const statusColors = {
  pending: { color: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
  contacted: { color: "#3B82F6", bg: "rgba(59,130,246,0.15)" },
  cleared: { color: "#059669", bg: "rgba(5,150,105,0.15)" },
};

const Dues = () => {
  const { receptionists } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    patient_name: "",
    contact: "",
    amount_due: "",
    remarks: "",
    next_visit: "",
    status: "pending",
    assigned_employee_id: "",
  });

  const load = () => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.append("status", statusFilter);
    api.get(`/due-patients?${params.toString()}`).then((r) => setRows(r.data));
  };

  useEffect(load, [statusFilter]);

  const create = async () => {
    if (!form.patient_name) return toast.error("Patient name required");
    try {
      await api.post("/due-patients", {
        ...form,
        amount_due: Number(form.amount_due || 0),
      });
      toast.success("Due patient added");
      setOpen(false);
      setForm({
        patient_name: "",
        contact: "",
        amount_due: "",
        remarks: "",
        next_visit: "",
        status: "pending",
        assigned_employee_id: "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const changeStatus = async (id, status) => {
    await api.put(`/due-patients/${id}`, { status });
    load();
  };

  const totalOutstanding = rows.reduce((a, r) => a + (r.status !== "cleared" ? Number(r.amount_due || 0) : 0), 0);
  const overdueBadge = (days) => {
    if (days >= 30) return <Badge className="bg-rose-500/15 text-rose-400 border border-rose-500">30d+</Badge>;
    if (days >= 15) return <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500">15d+</Badge>;
    if (days >= 7) return <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500">7d+</Badge>;
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Payment Follow-Up</div>
          <h1 className="font-heading text-4xl text-white tracking-tight mt-1">Due Patient Tracker</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Live, filterable list of pending patient dues. Auto-flags entries overdue by 7 / 15 / 30 days.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-due-btn"
              className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11 px-5"
            >
              <Plus size={16} className="mr-2" /> Add Due Patient
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">New Due Patient</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                Log a new outstanding payment. Overdue reminders fire at 7 / 15 / 30 days.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Patient Name" testid="due-name">
                <Input
                  data-testid="due-name"
                  value={form.patient_name}
                  onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </FormField>
              <FormField label="Contact">
                <Input
                  data-testid="due-contact"
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </FormField>
              <FormField label="Amount Due (₹)">
                <Input
                  data-testid="due-amount"
                  type="number"
                  value={form.amount_due}
                  onChange={(e) => setForm({ ...form, amount_due: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </FormField>
              <FormField label="Next Visit">
                <Input
                  data-testid="due-next-visit"
                  type="date"
                  value={form.next_visit}
                  onChange={(e) => setForm({ ...form, next_visit: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </FormField>
              <FormField label="Assigned Receptionist">
                <Select
                  value={form.assigned_employee_id}
                  onValueChange={(v) => setForm({ ...form, assigned_employee_id: v })}
                >
                  <SelectTrigger data-testid="due-assign" className="bg-[#0F172A] border-[#334155] text-white">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                    {receptionists.map((r) => (
                      <SelectItem key={r.employee_id} value={r.employee_id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="due-status" className="bg-[#0F172A] border-[#334155] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="cleared">Cleared</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div>
              <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">Remarks</Label>
              <Textarea
                data-testid="due-remarks"
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                className="bg-[#0F172A] border-[#334155] text-white"
              />
            </div>
            <Button
              onClick={create}
              data-testid="due-save-btn"
              className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium"
            >
              Save
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#1E293B] border-[#334155] p-5">
          <div className="overline">Outstanding</div>
          <div className="font-heading text-3xl text-white mt-2">₹{totalOutstanding.toLocaleString("en-IN")}</div>
          <div className="text-xs text-slate-500 mt-1">{rows.filter((r) => r.status !== "cleared").length} open records</div>
        </Card>
        <Card className="bg-[#1E293B] border-[#334155] p-5">
          <div className="overline">Overdue 15+ days</div>
          <div className="font-heading text-3xl text-amber-400 mt-2">
            {rows.filter((r) => r.status !== "cleared" && r.days_overdue >= 15 && r.days_overdue < 30).length}
          </div>
        </Card>
        <Card className="bg-[#1E293B] border-[#334155] p-5">
          <div className="overline">Overdue 30+ days</div>
          <div className="font-heading text-3xl text-rose-400 mt-2">
            {rows.filter((r) => r.status !== "cleared" && r.days_overdue >= 30).length}
          </div>
        </Card>
      </div>

      <Card className="bg-[#1E293B] border-[#334155] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-[#C9A24B]" />
            <h3 className="font-heading text-xl text-white">Due Patients</h3>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="due-status-filter" className="w-40 bg-[#0F172A] border-[#334155] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="cleared">Cleared</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-lg border border-[#334155] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#334155] hover:bg-transparent">
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Patient</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Contact</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase text-right">Amount</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Remarks</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Overdue</TableHead>
                <TableHead className="text-slate-400 font-mono text-xs uppercase">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const c = statusColors[r.status] || statusColors.pending;
                return (
                  <TableRow key={r.due_id} className="border-[#334155] hover:bg-slate-800/40">
                    <TableCell className="text-white">{r.patient_name}</TableCell>
                    <TableCell className="text-slate-400 font-mono text-xs">{r.contact || "—"}</TableCell>
                    <TableCell className="text-right text-white font-mono">₹{Number(r.amount_due).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-slate-400 max-w-xs truncate">{r.remarks || "—"}</TableCell>
                    <TableCell>{overdueBadge(r.days_overdue || 0)}</TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.due_id, v)}>
                        <SelectTrigger
                          data-testid={`due-status-${r.due_id}`}
                          className="w-32 h-8 text-xs bg-[#0F172A] border-[#334155]"
                          style={{ color: c.color }}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="cleared">Cleared</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    No dues in this view.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

const FormField = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">{label}</Label>
    {children}
  </div>
);

export default Dues;
